import { createSelector } from '@reduxjs/toolkit'
import type { NodeV2 } from '@vedalib/editor'
import { isNodeV2, isShellNode } from '@vedalib/editor'

import { getShellByElementId } from 'components/Editor/EditorContext/selectors/block'
import type { IProjectContext, Block } from 'services/Store/Project/types'

import { getBlock, getBlocksMap } from '../data/block'
import { getNodeId } from '../url'
import { getSections } from './section'

const getNode = (block: Block, id: string) => block?.schema?.nodes?.[id]

export const getActiveNode = createSelector(getNodeId, getBlock, (nodeId, block): NodeV2 | null => {
  const node = nodeId && block && block?.schema.nodes[nodeId]
  if (!isNodeV2(node)) {
    return null
  }
  return node
})

export const getActiveElementId = (state: IProjectContext) => {
  const nodeId = getNodeId(state)
  return nodeId ? getElementId(state, { nodeId }) : null
}

export const getElement = (state: IProjectContext, params?: { id?: string }) => {
  const block = getBlock(state)
  const id = params?.id || getActiveElementId(state) || ''
  return block?.elements[id] || null
}

export const getAllElementsByIds = (state: IProjectContext, params: { ids: string[] }) => {
  if (!params?.ids || params.ids.length === 0) {
    return []
  }
  const blocksMap = getBlocksMap(state)
  const allElements = Object.values(blocksMap).flatMap((block) =>
    Object.values(block.elements || {}).map((element) => ({
      element,
      block,
    })),
  )
  if (params?.ids && params.ids.length > 0) {
    const idsSet = new Set(params.ids)
    return allElements.filter(({ element }) => idsSet.has(element.id))
  }
  return allElements
}

export const getIsActiveElement = (state: IProjectContext, elementId: string) => {
  return getActiveElementId(state) === elementId
}

export const getElementId = (state: IProjectContext, { nodeId }: { nodeId: string }) => {
  const block = getBlock(state)
  if (!block) {
    return null
  }

  const node = getNode(block, nodeId)
  if (isShellNode(node)) {
    return node.elementId || null
  }

  return null
}

export const getCourseEndButtonElement = (state: IProjectContext) => {
  const sections = getSections(state)

  let elementWithBlockAndSection =
    sections
      .flatMap((section) =>
        (section.blocks || []).flatMap((block) =>
          Object.values(block.elements || {}).map((element) => ({
            element,
            block,
            sectionId: section.id,
          })),
        ),
      )
      .find(({ element }) => element.type === 'button' && element.value?.action === 'courseEnd') ||
    null

  if (!elementWithBlockAndSection) {
    const lastSection = sections[sections.length - 1]
    if (
      lastSection &&
      lastSection.blocksOrder &&
      lastSection.blocks &&
      lastSection.blocks.length > 0
    ) {
      const lastBlockId = lastSection.blocksOrder[lastSection.blocksOrder.length - 1]
      const lastBlock = lastSection.blocks.find((block) => block.uuid === lastBlockId)
      if (lastBlock && lastBlock.elements) {
        const elementIds = Object.keys(lastBlock.elements)
        const lastElementId = elementIds[elementIds.length - 1]
        const lastElement = lastBlock.elements[lastElementId]
        if (lastElement) {
          elementWithBlockAndSection = {
            element: lastElement,
            block: lastBlock,
            sectionId: lastSection.id,
          }
        }
      }
    }
  }

  const nodeId =
    elementWithBlockAndSection?.block &&
    getShellByElementId(elementWithBlockAndSection.block, elementWithBlockAndSection.element.id)

  return {
    element: elementWithBlockAndSection?.element,
    block: elementWithBlockAndSection?.block,
    nodeId,
    sectionId: elementWithBlockAndSection?.sectionId,
  }
}
